import { ShortenTimeUnit, SingularTimeUnit, SingularTimeUnitToPlural, TimeUnit, TimeUnitDuration,
	TimeUnitToSingular
} from "@/types/time"
import { DateTime, DateTimeFormatOptions } from "luxon"
import moment from "moment"

export const timeLocale: DateTimeFormatOptions = {
	hour: "2-digit",
	minute: "2-digit",
	hour12: true
}

export const formatDateMixin = {
	methods: {
		dateToString (value: DateTime) {
			return value.toLocaleString() + " " + value.toFormat("hh:mm a")
		},
		dateStringWithZeroHourOffset (value: string) {
			return value ? DateTime.fromISO(value + "Z") : ""
		},
		displayDateFromISOString (value: string | null, store: any = null) {
			if ((this as any).$store?.state?.displayTimezone) {
				// do nothing. We just need to reference the display timezone to re-render on change
			} else if (store?.state?.displayTimezone) {
			}

			return value
				? this.dateToString(DateTime.fromISO(value!))
				: ""
		},
		displayTimezoneDateFromISOString (value: string | null, timeZone: string) {
			if (!value) return ""

			return this.dateToString(DateTime.fromISO(value!).setZone(timeZone))
		},
		displayDateOnlyFromISOString (value: string | null, store: any = null) {
			if ((this as any).$store?.state?.displayTimezone) {
				// do nothing. We just need to reference the display timezone to re-render on change
			} else if (store?.state?.displayTimezone) {
			}

			return value ? DateTime.fromISO(value!).toFormat("D") : ""
		},
		displayDateOnlyFromJsDate (value: Date | null, store: any = null) {
			if ((this as any).$store?.state.displayTimezone) {
				// do nothing. We just need to reference the display timezone to re-render on change
			} else if (store?.state.displayTimezone) {
			}

			return value ? DateTime.fromJSDate(value!).toFormat("D") : ""
		},
		displayUtcDateOnlyFromJsDate (value: Date | null) {
			return value ? DateTime.fromJSDate(value, { zone: "utc" }).toFormat("D") : ""
		},
		displayUtcDateOnlyFromISOString (value: string | null) {
			return value ? DateTime.fromISO(value!, { zone: "utc" }).toFormat("D") : ""
		},
		convertUtcDateTimeToLocalDateTimeString (value: Date) {
			return value ? formatDateMixin.methods.dateToString(DateTime.fromISO(value + "Z")) : ""
		},
		convertUtcDateTimeToLocalTimeString (value: Date) {
			return value ? DateTime.fromISO(value + "Z").toFormat("t a") : ""
		},
		fromNow (value: Date) {
			return value ? moment(value + "Z").fromNow() : ""
		},
		isoFromNow (value: Date) {
			return value ? moment(value).fromNow() : ""
		},
		currentDateFormatted () {
			return moment().format()
		},
		endOFDayFormatted () {
			return moment()
				.endOf("day")
				.format()
		},
		weekAgoDateFormatted () {
			return moment()
				.subtract(7, "days")
				.format()
		},
		toStartOfDay (value: string) {
			return moment(value)
				.startOf("day")
				.format()
		},
		toEndOfDay (value: string) {
			return moment(value)
				.endOf("day")
				.format()
		},
		relativeDaysDateFormatted (days: number) {
			days = days === undefined ? 7 : days
			return moment()
				.subtract(days, "days")
				.format("YYYY-MM-DDTHH:mm:ss.000")
		},
		formatDateStr (dateStr: string, timeOnly = false, convertFromUTC = false, includePeriod = true, dateOnly = false) {
			if (dateStr) {
				if (convertFromUTC && !dateStr.includes("Z")) {
					dateStr += "Z"
				}

				const date = new Date(dateStr)

				const day = date.getDate()
				const monthIndex = date.getMonth()
				const year = date.getFullYear()
				const hours = date.getHours()
				const mins = date.getMinutes()

				let formattedDate = ""

				if (!timeOnly) {
					formattedDate += day + " " + MONTH_NAMES[monthIndex] + " " + year
				}

				if (!dateOnly) {
					formattedDate += " " + (hours < 10 ? "0" : "") + hours + ":" + (mins < 10 ? "0" : "") +
						mins + " " + (includePeriod ? (hours < 12 ? "am" : "pm") : "")
				}
				return formattedDate.trim()
			}
		},
		/**
		 * Formats a quantity of time into weeks, days, hours and minutes, or a subset of these units e.g. hours and mins.
		 * @param timeSpanMs The quantity of time in milliseconds.
		 * @param options Options for formatting the time span output. See FormattedTimeSpanDisplayOptions for details.
		 */
		getFormattedTimeSpanDisplay (timeSpanMs: number,
			options: FormattedTimeSpanDisplayOptions = defaultFormattedTimeSpanDisplayOptions
		): string {
			// Remove any undefined option values from the provided options object.
			Object.keys(options).forEach(optionKey => {
				const typedOptionKey = optionKey as keyof FormattedTimeSpanDisplayOptions
				if (options[typedOptionKey] === undefined) delete options[typedOptionKey]
			})

			// Set default values for any missing options.
			options = { ...defaultFormattedTimeSpanDisplayOptions, ...options }

			const greatestUnitIndex = TimeUnitDuration.findIndex(u => u.unit === options.greatestUnit)
			let smallestUnitIndex = TimeUnitDuration.findIndex(u => u.unit === options.smallestUnit)

			if (greatestUnitIndex > smallestUnitIndex) {
				// Ensure that the smallest unit is not greater than the greatest unit.
				options.smallestUnit = options.greatestUnit
				smallestUnitIndex = greatestUnitIndex
			}

			const filteredUnits = TimeUnitDuration.filter(unit => {
				const unitIndex = TimeUnitDuration.findIndex(u => u.unit === unit.unit)
				return unitIndex >= greatestUnitIndex && unitIndex <= smallestUnitIndex
			})

			const roundToClosestUnit = TimeUnitDuration.find(u => u.singularUnit === options.roundToClosest!)
			const roundToClosestUnitMs = roundToClosestUnit ? roundToClosestUnit.milliseconds : 1

			// Round to a closest unit if requested.
			let remainingMs = Math.round(timeSpanMs / roundToClosestUnitMs) * roundToClosestUnitMs

			const parts = []
			for (const { singularUnit, milliseconds } of filteredUnits) {
				const unit = singularUnit
				const unitCount = Math.floor(remainingMs / milliseconds)

				if (unitCount > 0) {
					const unitName = options.shortenedUnits
						? ShortenTimeUnit(unit)
						: unit

					const unitAppend = unitCount === 1 ? unitName : SingularTimeUnitToPlural(unitName)

					parts.push(`${unitCount} ${unitAppend}`)
					remainingMs %= milliseconds
				}
			}

			if (parts.length === 0) {
				if (options.belowThresholdTextOverride) {
					return options.belowThresholdTextOverride
				}

				const smallestUnitSingular = TimeUnitToSingular(options.smallestUnit!)

				const lessThanPrefix = options.shortenedUnits ? "< 1 " : "Less than a "
				const smallestUnitName = options.shortenedUnits
					? ShortenTimeUnit(smallestUnitSingular)
					: smallestUnitSingular

				return lessThanPrefix + smallestUnitName
			}

			const delimiter = options.delimiterOverride ?? ", "

			return parts.join(options.includeDelimiter ? delimiter : " ")
		},
		/**
		 * Gets the difference between two dates in days, hours, minutes
		 * @param startDate The start date string
		 * @param endDate The end date string
		 */
		getFormattedTimeSpanBetweenDatesDisplay (startDate: string, endDate: string): string {
			const start = new Date(startDate)
			const end = new Date(endDate)

			const diff = end.getTime() - start.getTime()

			return this.getFormattedTimeSpanDisplay(diff)
		},
		/**
		 * Returns the 3-letter day of the week (e.g., "Mon", "Tue") for a given JavaScript Date object,
		 * using the specified time zone.
		 *
		 * @param value The JavaScript Date object to extract the day from.
		 * @param timeZone The IANA time zone identifier to interpret the date in (defaults to "utc").
		 * @returns The short day of the week string based on the specified time zone, or an empty string if value is null.
		 */
		getDayShortFromJsDate (value: Date | null, timeZone = "utc") {
			return value ? DateTime.fromJSDate(value, { zone: timeZone }).toFormat("ccc") : ""
		},
		/**
		 * Returns the 3-letter day of the week (e.g., "Mon", "Tue") for a given ISO date string,
		 * using the specified time zone.
		 *
		 * @param value The ISO date string to extract the day from.
		 * @param timeZone The IANA time zone identifier to interpret the date in (defaults to "utc").
		 * @returns The short day of the week string based on the specified time zone, or an empty string if value is null.
		 */
		getDayShortFromISOString (value: string | null, timeZone = "utc") {
			return value ? DateTime.fromISO(value, { zone: timeZone }).toFormat("ccc") : ""
		},
		/**
		 * Returns whether a given string matches the format of a date and time (e.g. DD/MM/YYYY HH:MM:SS a), where 'a' is optional AM/PM.
		 * This does not check whether the date and time are valid (e.g. '50/50/2021 99:99:99' is valid), only that they are formatted correctly.
		 * @param str The string to check
		 * @returns Whether the string matches the format of a formatted date and time
		 */
		stringMatchesFriendlyDateAndTimeFormat (str: string): boolean {
			const formattedDateAndTimeRegex = /^\d{1,2}\/\d{1,2}\/\d{4} \d{1,2}:\d{2}:\d{2}(?: [AP]M)?$/
			return formattedDateAndTimeRegex.test(str)
		},
		/**
		 * Formats a date string to display the short day name and ordinal day number (e.g., "Mon 1st", "Tue 22nd").
		 * @param dateString The date string to format
		 * @returns The formatted date string with day name and ordinal suffix, or an empty string if dateString is falsy
		 */
		formatDateToDayDD (dateString: string): string {
			if (!dateString) {
				return ""
			}
			const date = new Date(dateString)
			const dayOfMonth = date.getDate()
			const dayName = date.toLocaleDateString(undefined, { weekday: "short" })

			// Add ordinal suffix (st, nd, rd, th)
			const suffix = (dayOfMonth: number) => {
				if (dayOfMonth > 3 && dayOfMonth < 21) {
					return "th"
				}
				switch (dayOfMonth % 10) {
					case 1: return "st"
					case 2: return "nd"
					case 3: return "rd"
					default: return "th"
				}
			}

			return `${dayName} ${dayOfMonth}${suffix(dayOfMonth)}`
		}
	}
}

export const stringMixin = {
	methods: {
		isNullOrWhitespace (input: string) {
			return !input || !input.trim()
		}
	}
}

export const MONTH_NAMES = ["Jan", "Feb", "March", "April", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"]

/**
 * Options for formatting days, hours, minutes and milliseconds into a user readable display string.
 * @param includeDelimiter Whether to include delimiters between the parts of the formatted string. Defaults to false.
 * @param shortenedUnits Whether to use shortened units (e.g. "min" instead of "minute"). Defaults to true.
 * @param greatestUnit The greatest unit of time to include in the formatted string. Defaults to days.
 * @param smallestUnit The smallest unit of time to include in the formatted string. Defaults to minutes.
 * Note that greatestUnit takes precedence over smallestUnit.
 * @param roundToClosestSecond Whether to round the time span to the closest second to mitigate rounding errors. Defaults to false.
 * @param belowThresholdTextOverride The text to return if the time span is below the threshold of the smallest unit. Otherwise uses "Less than a ...".
 * @param delimiterOverride The delimiter to use between the parts of the formatted string if includeDelimiter is enabled. Defaults to ", ".
*/
export interface FormattedTimeSpanDisplayOptions {
	includeDelimiter?: boolean
	shortenedUnits?: boolean
	greatestUnit?: TimeUnit
	smallestUnit?: TimeUnit
	roundToClosest?: SingularTimeUnit
	belowThresholdTextOverride?: string | null
	delimiterOverride?: string | null
}

const defaultFormattedTimeSpanDisplayOptions: FormattedTimeSpanDisplayOptions = Object.freeze({
	// Note: make sure to update the doc for FormattedTimeSpanDisplayOptions above if changing default values

	includeDelimiter: false,
	shortenedUnits: true,
	greatestUnit: TimeUnit.Days,
	smallestUnit: TimeUnit.Minutes,
	roundToClosest: SingularTimeUnit.Millisecond,
	belowThresholdTextOverride: null,
	delimiterOverride: null
})
